/**
 * B117 slice 2 — Form and the closed-set pickers. FORMS.md R11: a form
 * names its outcome before and after; the primary is the ONLY accent
 * control, disabled until the form is valid and something changed,
 * aria-busy while working, and failure preserves every entered value.
 * R4: a value that must be one of a known set is a picker fed by REAL
 * data — a text field that accepts USD also accepts usd, dollars, and
 * nothing. R7: an operator never types, sees, or copies an id.
 */
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Banner, Select, SegmentedControl } from "./components.js";
import type { FieldWidth } from "./forms.js";

/* --------------------------------------------------------------- Form */

/**
 * Owns the form chrome the apps kept hand-rolling: dirty tracking (any
 * change inside the form counts; pass `dirty` to control it), the
 * submit gate, and the failure banner. Field-level errors stay on the
 * fields; `error` is for the failure of the ATTEMPT, in operator words,
 * with everything the operator typed still on screen.
 */
export function Form(props: {
  /** Names the outcome — "Create price list", never Submit/Save/OK (R11). */
  submitLabel: string;
  onSubmit: () => void;
  /** The consumer owns validity (it knows its fields); the Form owns the gate. */
  valid: boolean;
  busy?: boolean;
  /** What failed and what to do, in operator words. Renders role=alert. */
  error?: string;
  /** Override internal dirty tracking (edit flows, tests). */
  dirty?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  /**
   * B593 (FORMS.md R14) — why the submit is shut, in the operator's words;
   * drawn beside it while it is shut for being invalid. An untouched valid
   * form says "Nothing has changed yet." on its own.
   */
  why?: string | undefined;
  children: ReactNode;
}): ReactNode {
  const [touched, setTouched] = useState(false);
  const whyId = useId();
  const busy = props.busy === true;
  const dirty = props.dirty ?? touched;
  const canSubmit = props.valid && dirty && !busy;
  const why = busy || canSubmit ? undefined : !props.valid ? props.why : "Nothing has changed yet.";
  return (
    <form
      className="t-form"
      onChange={() => setTouched(true)}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) props.onSubmit();
      }}
    >
      {props.children}
      {/* B213 burn-down: this site hand-rolled Banner's own markup
          (`t-banner t-banner--danger` + role="alert") instead of importing
          it — the cheapest burn on the bare-alert list, and the rendered
          output is unchanged by construction because Banner IS that markup. */}
      {props.error === undefined ? null : <Banner tone="danger">{props.error}</Banner>} {/* wayback-ok: a form refusal — the form beneath is the way back (FORMS.md) and every submit is a retry; the operator's entries must survive it */}
      <div className="t-form-actions">
        {/* B300 leg (a). This Cancel was SHUT while the form's own submit was
            in flight, which is the face B299 ruled on in the apps: an act in
            flight is not a refusal, so a grey control tells the operator the
            screen took something away. Opening it is not the cure either — an
            open Cancel that cannot recall the request already sent is markup
            pretending to be a promise. `OneWayDoorFace` answered this exact
            question one file over, in this same package, and its words are the
            ruling: no way back is drawn while the act is in the air, "because
            there is none to offer". The way back is ABSENT here, and it comes
            straight back the moment the act lands. */}
        {busy || props.cancelLabel === undefined || props.onCancel === undefined ? null : (
          <button type="button" className="t-btn t-btn--neutral" onClick={props.onCancel}>
            {props.cancelLabel}
          </button>
        )}
        <button
          type="submit"
          className="t-btn t-btn--primary"
          aria-describedby={why !== undefined ? whyId : undefined}
          disabled={!canSubmit}
          aria-busy={busy ? "true" : undefined}
        >
          {props.submitLabel}
        </button>
      </div>
      {why !== undefined ? (
        <p className="t-footnote" id={whyId}>
          {why}
        </p>
      ) : null}
    </form>
  );
}

/* ------------------------------------------------------- ChannelPicker */

export interface ChannelChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * "Applies to" over the tenant's REAL channels. Segmented at ≤4 options,
 * a Select above that (R4). Value "" means all channels — the option
 * says so in words; "blank = tenant default" never renders again.
 */
export function ChannelPicker(props: {
  id: string;
  channels: readonly ChannelChoice[];
  /** "" = all channels. */
  value: string;
  onChange: (channelId: string) => void;
  label?: string;
  /** Offer the "All channels" option (value ""). Default true. */
  includeAll?: boolean;
  hint?: string;
  error?: string;
  disabled?: boolean;
}): ReactNode {
  const label = props.label ?? "Applies to";
  const options = [
    ...(props.includeAll === false ? [] : [{ value: "", label: "All channels" }]),
    ...props.channels.map((c) => ({ value: c.id, label: c.name })),
  ];
  if (options.length <= 4) {
    return (
      <SegmentedControl
        legend={label}
        name={props.id}
        options={options}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled === true}
      />
    );
  }
  return (
    <Select
      id={props.id}
      label={label}
      options={options}
      value={props.value}
      onChange={props.onChange}
      {...(props.hint !== undefined ? { hint: props.hint } : {})}
      {...(props.error !== undefined ? { error: props.error } : {})}
      disabled={props.disabled === true}
      width="md"
    />
  );
}

/* ------------------------------------------------------ CurrencyPicker */

function currencyName(code: string): string {
  try {
    const name = new Intl.DisplayNames("en", { type: "currency" }).of(code);
    return name === undefined || name === code ? code : `${name} (${code})`;
  } catch {
    return code;
  }
}

/** "Currency" over the currencies the tenant can actually use — never free text, never a standards name. */
export function CurrencyPicker(props: {
  id: string;
  currencies: readonly string[];
  value: string;
  onChange: (code: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** B355 — the your-business frame draws the pair filling the clock row's halves. */
  width?: FieldWidth;
}): ReactNode {
  const codes = props.currencies.includes(props.value)
    ? props.currencies
    : [...props.currencies, props.value];
  return (
    <Select
      id={props.id}
      label={props.label ?? "Currency"}
      options={codes.map((code) => ({ value: code, label: currencyName(code) }))}
      value={props.value}
      onChange={props.onChange}
      {...(props.hint !== undefined ? { hint: props.hint } : {})}
      {...(props.error !== undefined ? { error: props.error } : {})}
      disabled={props.disabled === true}
      width={props.width ?? "md"}
    />
  );
}

/* ------------------------------------------------------ TimezonePicker */

/**
 * B579 — the zones an operator is most likely to mean, for a picker with no
 * tenant history to lead it (the demo builder reading a stranger's site;
 * your-business's own list leads with the browser's zone first).
 */
export const MAJOR_TIME_ZONES: readonly string[] = Object.freeze([
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Paris",
  "Australia/Sydney",
  "Asia/Singapore",
]);

/** B579 — a zone said the way the picker says it: "Los Angeles (GMT-7)". */
export function zoneWords(zone: string, now: number = Date.now()): string {
  return zoneLabel(zone, now);
}

function zoneLabel(zone: string, now: number): string {
  const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(
      new Date(now),
    );
    const offset = parts.find((p) => p.type === "timeZoneName")?.value;
    return offset === undefined ? city : `${city} (${offset})`;
  } catch {
    return city;
  }
}

/**
 * A zone picker whose VISIBLE text is a city and an offset — the IANA
 * id travels underneath in the value, never as words an operator reads.
 */
export function TimezonePicker(props: {
  id: string;
  /** The zones this tenant actually operates in — real data, not the full IANA list. */
  zones: readonly string[];
  value: string;
  onChange: (zone: string) => void;
  label?: string;
  /** Clock injection for deterministic offset labels (tests); defaults to now. */
  now?: number;
  /**
   * B579 — the words for "no zone yet", drawn as the leading option. Without
   * it an empty value would be appended as a nameless zone; with it the
   * blank is an honest, named choice the operator replaces.
   */
  emptyLabel?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** B355 — the your-business frame draws the pair filling the clock row's halves. */
  width?: FieldWidth;
}): ReactNode {
  const now = props.now ?? Date.now();
  const blank = props.emptyLabel !== undefined;
  const zones =
    props.zones.includes(props.value) || (blank && props.value === "") ? props.zones : [...props.zones, props.value];
  return (
    <Select
      id={props.id}
      label={props.label ?? "Time zone"}
      options={[
        ...(blank ? [{ value: "", label: props.emptyLabel as string }] : []),
        ...zones.map((zone) => ({ value: zone, label: zoneLabel(zone, now) })),
      ]}
      value={props.value}
      onChange={props.onChange}
      {...(props.hint !== undefined ? { hint: props.hint } : {})}
      {...(props.error !== undefined ? { error: props.error } : {})}
      disabled={props.disabled === true}
      width={props.width ?? "md"}
    />
  );
}

/* --------------------------------------------------- PickerTile (B280) */

/**
 * B280 — the compact option tile: a calendar day, a departure time, any
 * one-of-many pick too small for Tile's whole-card shape. The storefront
 * hand-rolled this three times behind drift-ok escapes whose "filed as
 * backlog work" claim was false; now it lives here. `pressed` renders
 * aria-pressed and is for toggle tiles (a selected day); act tiles (a
 * departure that navigates) omit it. `note` is the consequence chip —
 * "5 left", "Sold out", "Doesn't fit your party" — and `noteTone="low"`
 * warns without shouting. Disabled tiles stay visible: a sold-out
 * departure is information, not something to hide (FORMS.md R12 kin).
 */
export function PickerTile(props: {
  /** The tile's main line — a day-of-month, a time. */
  readonly value: string;
  /** Small line above the value — "Mon" over "20". */
  readonly kicker?: string;
  /** Consequence chip under the value — availability, fit. */
  readonly note?: string;
  /** `low` = the danger register (sold out / no fit); `warn` = 72:2's amber low-inventory turn. */
  readonly noteTone?: "muted" | "low" | "warn";
  /** Full human name for assistive tech, e.g. "Monday, July 20". */
  readonly label?: string;
  /** Toggle tiles pass it (renders aria-pressed); act tiles omit it. */
  readonly pressed?: boolean;
  /**
   * B403 wave-2 residual — the buyer sheet's pill (72:2: pad 8/14, no
   * min-width) so FIVE days fit the 390 window. The POS pill keeps its
   * counter register (redline-pos-sale: pad 10/18) — two frames, two
   * recipes, one component.
   */
  readonly compact?: boolean;
  readonly disabled?: boolean;
  readonly onPress: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={props.compact === true ? "t-picker-tile t-picker-tile--compact" : "t-picker-tile"}
      {...(props.pressed !== undefined ? { "aria-pressed": props.pressed } : {})}
      {...(props.label !== undefined ? { "aria-label": props.label } : {})}
      disabled={props.disabled === true}
      onClick={props.onPress}
    >
      {props.kicker === undefined ? null : <span className="t-picker-tile-kicker">{props.kicker}</span>}
      <span className="t-picker-tile-value">{props.value}</span>
      {props.note === undefined ? null : (
        <span
          className={
            props.noteTone === "low"
              ? "t-picker-tile-note t-picker-tile-note--low"
              : props.noteTone === "warn"
                ? "t-picker-tile-note t-picker-tile-note--warn"
                : "t-picker-tile-note"
          }
        >
          {props.note}
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------- PickerRail (B355 iv) */

/**
 * B355 leg (iv) — the horizontal pill rail the exemplar draws, and the
 * skin debt leg (i) recorded rather than hid.
 *
 * Leg (i) put the sailing pills in a `RowList` (`t-rows`) and said so at
 * the site: it took the list RESET (a bare `<ul>` would ship browser
 * bullets, the raw-default bar B62 set) and accepted the STACK that came
 * with it, because minting a rail meant editing `tokens.css` while B353
 * was live in that sheet. B353 has landed, so the debt is payable.
 *
 * Why a component and not a class the app spells: the UI law (RULING
 * 2026-07-26) is that a surface needing a piece of UI grows it HERE, and
 * `t-rows` is the precedent in the other direction — `RowList` exists so
 * no app writes `class="t-rows"` by hand. A rail spelled as a string in
 * `apps/pos` would be invisible to the storefront's `SlotPicker`, which
 * draws the same pills for the same reason and is the next consumer.
 *
 * The rail is the SAME list semantics as `RowList` — a `<ul>` of `<li>`,
 * reset, labelled for AT — and differs in exactly one fact: it flows
 * along the inline axis and wraps. That fact is the whole component,
 * which is why it is a sibling of `RowList` rather than a `direction`
 * prop on it: a caller choosing `direction="row"` on a thing named
 * RowList is a name that has stopped meaning anything.
 *
 * It WRAPS rather than scrolls. A counter's sailing set is small and a
 * seller mid-queue must see every option at once; a horizontal scroller
 * hides options behind a gesture, and an option you cannot see is one
 * you do not sell (the B287 finding — correct and invisible is not
 * correct).
 */
export function PickerRail(props: {
  /** names the rail for AT where no visible heading already does */
  readonly label?: string;
  /** points at the visible heading that already names it (B285's aria pairing) */
  readonly describedBy?: string;
  /**
   * B397 review #1 (2b) — the 390 sheet's rail is a WINDOW: one row on the
   * inline axis that scrolls, never the whole season stacked (72:2 draws a
   * 5-pill window). The DEFAULT stays the POS wrap: a seller mid-queue must
   * see every sailing at once (B287 — correct and invisible is not correct),
   * and that argument is the counter's, not the buyer's phone's.
   */
  readonly window?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  /* B403 #24 — the season continues in the SAME row: the window carries
     its two chevron doors (scroll by one viewport of pills; the browser
     clamps the ends), and the row is width-CONTAINED so forty days can
     never stretch the card into forty pills wide — the measured
     data-conditional miss. The POS wrap keeps its everything-at-once
     face untouched (B287's argument is the counter's). */
  const railRef = useRef<HTMLUListElement | null>(null);
  /* B403 wave-2 residual — the doors OVERLAY the row's ends instead of
     renting width from it (the frame draws none, and 390 has no spare
     column: doors-in-flow left room for four pills where 72:2 fits five).
     They draw only when the season actually overflows the window; a
     five-day season keeps the frame's doorless face. */
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = railRef.current;
    if (el === null) return;
    const measure = (): void => {
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  });
  const nudge = (direction: -1 | 1): void => {
    const el = railRef.current;
    if (el === null) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };
  const rail = (
    <ul
      ref={props.window === true ? railRef : undefined}
      className={props.window === true ? "t-picker-rail t-picker-rail--window" : "t-picker-rail"}
      {...(props.label !== undefined ? { "aria-label": props.label } : {})}
      {...(props.describedBy !== undefined ? { "aria-describedby": props.describedBy } : {})}
    >
      {props.children}
    </ul>
  );
  if (props.window !== true) return rail;
  return (
    <div className="t-picker-window" data-overflow={overflowing ? "true" : "false"}>
      <button type="button" className="t-picker-chevron t-picker-chevron--earlier" aria-label="Earlier" onClick={() => nudge(-1)}>
        {"\u2039"}
      </button>
      {rail}
      <button type="button" className="t-picker-chevron t-picker-chevron--later" aria-label="Later" onClick={() => nudge(1)}>
        {"\u203a"}
      </button>
    </div>
  );
}
